import { inject } from 'vue';
import type { Optional } from '~~/types/util';

export interface Toast {
  id: string;
  message: string;
  status: "success" | "error" | "warning" | "info";
  position: "top-center" | "bottom-center" | "top-right" | "bottom-right" | "top-left" | "bottom-left";
  duration: number;
}

export const initToast = () => {
  const toasts = reactive<Toast[]>([]);

  const pushSuccess = (param: Optional<Toast, 'id' | 'status' | 'position' | 'duration'>): Toast => {
    return push({
      ...param,
      status: 'success'
    });
  };

  const pushError = (param: Optional<Toast, 'id' | 'status' | 'position' | 'duration'>): Toast => {
    return push({
      ...param,
      status: 'error'
    });
  };

  const pushWarning = (param: Optional<Toast, 'id' | 'status' | 'position' | 'duration'>): Toast => {
    return push({
      ...param,
      status: 'warning'
    });
  };

  const pushInfo = (param: Optional<Toast, 'id' | 'status' | 'position' | 'duration'>): Toast => {
    return push({
      ...param,
      status: 'info'
    });
  };

  const push = (param: Optional<Toast, 'id' | 'position' | 'duration'>): Toast => {
    const toastEntry: Toast = {
      ...param,
      id: param.id || Math.random().toString(36).substring(7),
      position: param.position || 'top-right',
      duration: param.duration || 6000
    }

    toasts.push(toastEntry);
    
    setTimeout(() => {
      toasts.shift();
    }, toastEntry.duration);

    return toastEntry
  };

  const removeToast = (id: string) => {
    toasts.splice(toasts.findIndex((toast) => toast.id === id), 1);
  }

  
  return {
    pushSuccess,
    pushError,
    pushWarning,
    pushInfo,
    removeToast,
    toasts
  }
}

export default function useToast() {
  const useToast = inject<ReturnType<typeof initToast>>('useToast');
  
  if (!useToast) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return { 
    pushSuccess: useToast.pushSuccess,
    pushError: useToast.pushError,
    pushWarning: useToast.pushWarning,
    pushInfo: useToast.pushInfo,
    removeToast: useToast.removeToast,
   };
}